import * as React from "react";
import { useEffect, useState } from "react";
import { useDataProvider } from "react-admin";
import { Typography } from "@material-ui/core";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Legend,
  Bar,
  Tooltip,
  Line,
  ComposedChart,
} from "recharts";
import { SourceReliabilityMonthlyNff } from "./Constants";

interface MonthlyNffData {
  id: string;
  createdDate: string;
  dateMonth: string;
  unscheduledRemovalCount: number;
  completedRemovalCount: number;
  nffCount: number;
  nffRate?: number;
}

const MonthlyNffTrend = ({ filterParams }) => {
  const dataProvider = useDataProvider();
  const [chartData, setChartData] = useState<any[]>([]);

  useEffect(() => {
    GetChartData();
  }, [filterParams]);

  const GetChartData = async () => {
    await dataProvider
      .getList(SourceReliabilityMonthlyNff, {
        pagination: { page: 1, perPage: 99999 },
        sort: { field: "dateMonth", order: "ASC" },
        filter: filterParams,
      })
      .then(({ data }) => {
        // Calculate NFF Rate percentage for each month
        const dataWithRate = data.map((item: any) => ({
          ...item,
          nffRate:
            item.completedRemovalCount > 0
              ? parseFloat(((item.nffCount / item.completedRemovalCount) * 100).toFixed(2))
              : 0,
          // Calculate completed removals (excluding unscheduled)
          inprogressCount: item.unscheduledRemovalCount - item.completedRemovalCount,
        }));
        setChartData(dataWithRate);
      })
      .catch(error => {
        console.log(error);
      });
  };

  // Custom tooltip to show NFF Rate
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            padding: "10px",
            borderRadius: "4px",
          }}
        >
          <p style={{ margin: 0, fontWeight: "bold" }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: "4px 0", color: entry.color }}>
              {entry.name}: {entry.value}
              {entry.dataKey === "nffRate" && "%"}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Don't render if no data
  if (!chartData || chartData.length === 0) {
    return null;
  }

  return (
    <>
      <Typography gutterBottom variant="h5" component="h2" align="center" style={{ marginTop: 16, marginBottom: 24 }}>
        Monthly NFF Trend
      </Typography>
      <div style={{ width: "100%", height: 500, display: "flex", justifyContent: "center", alignItems: "center" }}>
        <div style={{ width: 800, height: "100%" }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 20, right: 80, left: 40, bottom: 100 }}
              barGap={10}
              barCategoryGap="20%"
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dateMonth" tick={{ fontSize: 14 }} height={50} />
              <YAxis
                yAxisId="left"
                orientation="left"
                tick={{ fontSize: 12 }}
                label={{ value: "Count", angle: -90, position: "insideLeft", style: { fontSize: 14 } }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 12 }}
                label={{ value: "NFF Rate (%)", angle: 90, position: "insideRight", style: { fontSize: 14 } }}
              />
              <Tooltip content={<CustomTooltip active={false} payload={[]} label={""} />} />
              <Legend
                verticalAlign="bottom"
                height={70}
                wrapperStyle={{ paddingTop: "20px", fontSize: "14px" }}
                iconType="square"
              />
              <Bar
                yAxisId="left"
                dataKey="completedRemovalCount"
                fill="#E53935"
                name="Completed Removals"
                barSize={60}
                stackId="removals"
              />
              <Bar
                yAxisId="left"
                dataKey="inprogressCount"
                fill="#424242"
                name="In Progress"
                barSize={60}
                stackId="removals"
              />
              <Bar yAxisId="left" dataKey="nffCount" fill="#FFA726" name="NFF Count" barSize={60} />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="nffRate"
                stroke="#0288D1"
                strokeWidth={3}
                name="NFF Rate (%)"
                dot={{ r: 6, fill: "#0288D1", strokeWidth: 2 }}
                activeDot={{ r: 8 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
};

export default MonthlyNffTrend;
